import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'

import makeStyles from '@mui/styles/makeStyles'
import { Button } from '@mui/material'

import { useRouter } from '@shared/hooks/use-router'
import { apiJSON } from '@shared/utils/api/api.util'

import { Decision, Roles } from '@shared/types'
import { ConfirmationDialog } from '@shared/components/confirmation.dialog.component'
import { useResourceRemove } from '@shared/hooks/use-resource-remove.hook'
import { Table } from '@shared/components/table/table.component'
import { originalIcons } from '@shared/components/icons/icons'
import { isArray } from 'lodash'
import { MaterialTableProps } from '@material-table/core'
import { useACL } from '@shared/hooks/use-acl.hook'
import { DecisionsDialog } from '@modules/decisions/decisions.dialog.component'
import handleDownload from '@shared/utils/handle-download'
import handleOpenFile from '@shared/utils/handle-open-file'

export const decisionsEndpoint = `decisions`

export function DecisionsPage() {
  const { is } = useACL()
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const isModifyAllowed = is([
    Roles.Admin,
    Roles.Auditor,
    Roles.ClientAdministrator,
    Roles.ClientEditor,
  ])
  const {
    query: { companyID },
    navigate,
    location,
  } = useRouter<{ companyID: string }>()

  const {
    isRemovalDialogOpened,
    isResourceRemoving,
    setRemovalDialogOpened,
    setRemovedResourceID,
    onResourceRemove,
    isInvalidatingAfterRemove,
    isResourceRemoveSucceed,
  } = useResourceRemove(`companies/${companyID}/Decisions`)

  const {
    data: decisions,
    isLoading: areDecisionsLoading,
    refetch,
  } = useQuery<Decision[]>(decisionsEndpoint, () =>
    apiJSON(`companies/${companyID}/Decisions`)
  )

  const handleReloadAfterCreate = () => {
    refetch()
    setIsAddDialogOpen(false)
  }

  useEffect(() => {
    if (isResourceRemoveSucceed) refetch()
  }, [isResourceRemoveSucceed])

  return (
    <>
      {isAddDialogOpen ? (
        <DecisionsDialog
          companyID={companyID}
          onClose={() => setIsAddDialogOpen(false)}
          afterCreate={handleReloadAfterCreate}
        />
      ) : null}
      <Table<Decision>
        isLoading={areDecisionsLoading || isInvalidatingAfterRemove}
        title="Decyzje"
        columns={[{ title: 'Nazwa', field: 'name' }]}
        data={decisions ?? []}
        options={{
          pageSize: 20,
        }}
        actions={
          isModifyAllowed
            ? ([
                {
                  icon: originalIcons.Download,
                  tooltip: 'Pobierz',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity: Decision
                  ) => {
                    if (!isArray(resourceEntity)) {
                      handleDownload(
                        `companies/${companyID}/Decisions/${resourceEntity.id}/download`,
                        resourceEntity?.fileName
                      )
                    }
                  },
                },
                {
                  icon: originalIcons.Visibility,
                  tooltip: 'Podgląd',
                  onClick: async (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity: Decision
                  ) => {
                    handleOpenFile(
                      `companies/${companyID}/Decisions/${resourceEntity.id}`
                    )
                  },
                },
                {
                  icon: originalIcons.Create,
                  tooltip: 'Edytuj',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity
                  ) => {
                    if (!isArray(resourceEntity)) {
                      navigate(
                        `/decisions/${companyID}/edit/${resourceEntity.id}`,
                        {
                          state: {
                            backgroundLocation: location,
                          },
                        }
                      )
                    }
                  },
                },
                {
                  icon: originalIcons.Delete,
                  tooltip: 'Usuń',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity
                  ) => {
                    if (!isArray(resourceEntity)) {
                      setRemovedResourceID(resourceEntity.id)
                      setRemovalDialogOpened(true)
                    }
                  },
                },
                {
                  icon: originalIcons.Add,
                  isFreeAction: true,
                  onClick: () => setIsAddDialogOpen(true),
                },
              ] as MaterialTableProps<any>['actions'])
            : ([
                {
                  icon: originalIcons.Download,
                  tooltip: 'Pobierz',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity
                  ) => {
                    if (!isArray(resourceEntity)) {
                      handleDownload(
                        `companies/${companyID}/Decisions/${resourceEntity.id}/download`,
                        resourceEntity?.fileName
                      )
                    }
                  },
                },
                {
                  icon: originalIcons.Visibility,
                  tooltip: 'Podgląd',
                  onClick: async (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity: Decision
                  ) => {
                    handleOpenFile(
                      `companies/${companyID}/Decisions/${resourceEntity.id}`
                    )
                  },
                },
              ] as MaterialTableProps<any>['actions'])
        }
      />
      <ConfirmationDialog
        submitButtonProps={{
          children: 'Usuń',
          isLoading: isResourceRemoving,
        }}
        title="Potwierdzenie usunięcia"
        onClose={onResourceRemove}
        open={isRemovalDialogOpened}
      >
        Czy napewno chcesz usunąć dany wiersz?
      </ConfirmationDialog>
      <Button
        variant="contained"
        onClick={() => navigate(-1)}
        style={{ backgroundColor: '#C0C0C0' }}
      >
        Powrót
      </Button>
    </>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    actionText: {
      textDecoration: 'underline',
      textAlign: 'right',
      cursor: 'pointer',
      display: 'inline-block',
      marginRight: theme.spacing(2),
    },
  }
})
